import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Container, Grid, Stack } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";

import AuthorizationConfirmButton from "@/components/authorization/AuthorizationConfirmButton.tsx";
import AuthorizationDatasetSelector from "@/components/authorization/AuthorizationDatasetSelector.tsx";
import AuthorizationResponsibilityBoxes from "@/components/authorization/AuthorizationResponsibilityBoxes.tsx";
import AuthorizationTermsAndConditions from "@/components/authorization/AuthorizationTermsAndConditions.tsx";
import {
  AuthorizationContext,
  useAuthorizeController,
} from "@/hooks/useAuthorizeController.tsx";

export const Route = createFileRoute("/flows/authorize/$identifier")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Authorize`,
      },
    ],
  }),
});

function RouteComponent() {
  const authorization = useAuthorizeController(Route.useParams().identifier);

  const navigate = Route.useNavigate();
  const onConfirm = useCallback(async () => {
    if (!authorization.project?.identifier) {
      return;
    }
    await authorization.insertAdded();
    await navigate({
      to: "/projects/$identifier",
      params: { identifier: authorization.project.identifier },
    });
  }, [authorization, navigate]);

  const { t } = useLingui();

  return (
    <AuthorizationContext.Provider value={authorization}>
      <Container size="lg">
        <Grid>
          <Grid.Col span={12}>
            <Stack>
              <p style={{ textAlign: "center", marginBottom: 0 }}>
                <Trans>You are ordering new permissions for project</Trans>
              </p>
              <strong style={{ textAlign: "center" }}>
                {authorization.project?.identifier}{" "}
                {authorization.project?.name}
              </strong>
            </Stack>
          </Grid.Col>
          <Grid.Col span={12}>
            <h3>
              <Trans>Terms and conditions</Trans>
            </h3>
            <AuthorizationTermsAndConditions />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              1. <Trans>Datasets</Trans>
            </h3>
            <AuthorizationDatasetSelector />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              2. <Trans>Responsibilities</Trans>
            </h3>
            <AuthorizationResponsibilityBoxes expectNewSelection />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              3. <Trans>Costs</Trans>
            </h3>
            <Stack gap="lg">
              <p>
                <Trans>
                  Currently, during the testing phase, there are no costs
                  associated with using this service
                </Trans>
              </p>
            </Stack>
          </Grid.Col>
          <Grid.Col span={4} offset={4}>
            <AuthorizationConfirmButton
              onConfirm={onConfirm}
              text={t`Confirm`}
              expectNewSelection
            />
          </Grid.Col>
        </Grid>
      </Container>
    </AuthorizationContext.Provider>
  );
}
